import { Moon, Sun, Contrast } from 'lucide-react'
import { useTheme, THEMES, type Theme } from '@/contexts/ThemeContext'

const ICON: Record<Theme, typeof Moon> = {
  dark:  Moon,
  dim:   Contrast,
  light: Sun,
}

const LABEL: Record<Theme, string> = {
  dark:  'Escuro',
  dim:   'Penumbra',
  light: 'Claro',
}

interface ThemeSwitcherProps {
  /** 'compact' = icon-only (sidebar). 'full' = icon + label (settings). */
  variant?: 'compact' | 'full'
}

export default function ThemeSwitcher({ variant = 'compact' }: ThemeSwitcherProps) {
  const { theme, cycleTheme } = useTheme()
  const Icon = ICON[theme]
  const nextTheme = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]

  const title = `Tema: ${LABEL[theme]} → clique para ${LABEL[nextTheme]}`

  if (variant === 'full') {
    return (
      <button
        onClick={cycleTheme}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] font-medium text-dim hover:text-bright transition-colors cursor-pointer"
        style={{ background: 'var(--color-neutral-pill-soft)' }}
        title={title}
      >
        <Icon className="w-3.5 h-3.5" />
        {LABEL[theme]}
      </button>
    )
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        // Brief transition-disable flag avoids colour flashes across the app.
        document.documentElement.setAttribute('data-theme-switching', '')
        cycleTheme()
        requestAnimationFrame(() => {
          requestAnimationFrame(() =>
            document.documentElement.removeAttribute('data-theme-switching')
          )
        })
      }}
      className="w-7 h-7 shrink-0 flex items-center justify-center rounded-md text-dim hover:text-bright transition-colors cursor-pointer"
      title={title}
      aria-label={title}
    >
      <Icon className="w-[15px] h-[15px]" />
    </button>
  )
}
